import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';

const PainelCobranca = () => {
    const navigate = useNavigate();
    
    // Estados Originais
    const [credores, setCredores] = useState([]);
    const [credorSelecionado, setCredorSelecionado] = useState('');
    const [dividas, setDividas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtroAtivo, setFiltroAtivo] = useState('TODAS');

    // 🔥 Novos Estados para o Feirão de Descontos
    const [selecionadas, setSelecionadas] = useState([]); 
    const [descontoFeirao, setDescontoFeirao] = useState(20); 
    const [dataLimiteFeirao, setDataLimiteFeirao] = useState(''); // Estado para a data limite

    // Carrega a lista de credores
    useEffect(() => {
        api.get('/assinante/credores')
            .then(res => setCredores(res.data))
            .catch(err => console.error("Erro ao carregar credores:", err));
    }, []);

    // Carrega as dívidas sempre que mudar o credor
    useEffect(() => {
        if (!credorSelecionado) {
            setDividas([]);
            return;
        }
        
        setLoading(true);
        api.get(`/assinante/dividas/inteligencia/${credorSelecionado}`)
            .then(res => {
                setDividas(res.data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Erro ao buscar dívidas:", err);
                setLoading(false);
            });
    }, [credorSelecionado]);

    // Limpa as seleções se mudar de aba ou de credor para não enviar ID errado
    useEffect(() => {
        setSelecionadas([]);
    }, [credorSelecionado, filtroAtivo]);

    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
    };

    const dividasFiltradas = dividas.filter(d => 
        filtroAtivo === 'TODAS' ? true : d.classificacaoAtraso === filtroAtivo
    );

    const qtdRiscoPrescricao = dividas.filter(d => d.classificacaoAtraso === 'RISCO_PRESCRICAO').length;

    // 🔥 Lógica de Seleção Múltipla
    const handleSelecionarTodas = (e) => {
        if (e.target.checked) {
            const ids = dividasFiltradas.map(d => d.dividaId);
            setSelecionadas(ids);
        } else {
            setSelecionadas([]);
        }
    };

    const handleSelecionarUma = (id) => {
        setSelecionadas(prev => 
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    // 🔥 Disparo em Lote para a API
    const aplicarFeiraoLote = async () => {
        if (selecionadas.length === 0) {
            alert("Selecione pelo menos uma dívida para incluir no Feirão.");
            return;
        }

        if (!dataLimiteFeirao) {
            alert("Por favor, defina a Data Limite de validade para o Feirão de Descontos.");
            return;
        }

        if (!window.confirm(`Confirma a aplicação de ${descontoFeirao}% de desconto para as ${selecionadas.length} dívidas selecionadas com validade até ${dataLimiteFeirao.split('-').reverse().join('/')}?`)) {
            return;
        }

        try {
            setLoading(true);
            await api.post('/assinante/dividas/aplicar-feirao', {
                dividaIds: selecionadas,
                percentualDesconto: descontoFeirao,
                dataLimite: dataLimiteFeirao // 🔥 Enviando a data formato YYYY-MM-DD para o Java
            });
            
            alert("Feirão aplicado com sucesso! Valores atualizados e notificações disparadas.");
            setSelecionadas([]); // Limpa as seleções
            
            // Recarrega as dívidas para mostrar os novos valores com desconto
            const res = await api.get(`/assinante/dividas/inteligencia/${credorSelecionado}`);
            setDividas(res.data);
        } catch (err) {
            console.error("Erro ao aplicar feirão:", err);
            // Mostra o erro exato retornado pelo Spring Boot se houver
            alert("Falha ao processar feirão: " + (err.response?.data?.erro || "Erro interno do servidor."));
        } finally {
            setLoading(false);
        }
    };

    const renderBadgeClassificacao = (classificacao) => {
        const badges = {
            'RECENTE': { bg: 'rgba(16, 185, 129, 0.1)', color: '#10b981', label: 'Recente' },
            'RECUPERACAO_CRITICA': { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', label: 'Feirão de Desconto' },
            'RISCO_PRESCRICAO': { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', label: 'Risco de Prescrição' },
            'PRESCRITA': { bg: 'rgba(100, 116, 139, 0.1)', color: '#94a3b8', label: 'Prescrita' },
            'JUDICIAL': { bg: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6', label: 'Judicial' }
        };
        const config = badges[classificacao] || badges['RECENTE'];
        return (
            <span style={{ background: config.bg, color: config.color, padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {config.label}
            </span>
        );
    };

    return (
        <div style={styles.container}>

            <style>
                {`
                    @keyframes piscarAlerta {
                        0% { background-color: rgba(239, 68, 68, 0.1); border-color: rgba(239, 68, 68, 0.3); box-shadow: 0 0 0 rgba(239, 68, 68, 0); }
                        50% { background-color: rgba(239, 68, 68, 0.3); border-color: rgba(239, 68, 68, 0.8); box-shadow: 0 0 20px rgba(239, 68, 68, 0.4); }
                        100% { background-color: rgba(239, 68, 68, 0.1); border-color: rgba(239, 68, 68, 0.3); box-shadow: 0 0 0 rgba(239, 68, 68, 0); }
                    }
                    .alerta-prescricao {
                        animation: piscarAlerta 2s infinite;
                    }
                    .checkbox-custom {
                        width: 18px;
                        height: 18px;
                        cursor: pointer;
                        accent-color: #f59e0b;
                    }
                    /* Remove as setinhas feias do input date no webkit */
                    input[type="date"]::-webkit-calendar-picker-indicator {
                        filter: invert(0.6) sepia(1) saturate(5) hue-rotate(35deg);
                        cursor: pointer;
                    }
                `}
            </style>

            <div style={styles.content}>
                
                <button onClick={() => navigate('/dashboard')} style={styles.btnVoltar}>
                    ← Voltar ao Dashboard
                </button>

                <header style={styles.header}>
                    <h1 style={styles.title}>Painel de Inteligência de Cobrança</h1>
                    <p style={{color: '#94a3b8'}}>Selecione a clínica/cliente para visualizar as oportunidades de recuperação.</p>
                </header>

                <div style={{ marginBottom: '30px' }}>
                    <select 
                        value={credorSelecionado} 
                        onChange={(e) => setCredorSelecionado(e.target.value)} 
                        style={styles.select}
                    >
                        <option value="">Selecione o Credor...</option>
                        {credores.map(c => (
                            <option key={c.id} value={c.id}>{c.nomeExibicao}</option>
                        ))}
                    </select>
                </div>

                {credorSelecionado && (
                    <>
                        {qtdRiscoPrescricao > 0 && (
                            <div 
                                className="alerta-prescricao" 
                                style={styles.bannerAlerta}
                                onClick={() => setFiltroAtivo('RISCO_PRESCRICAO')} 
                                title="Clique para filtrar a tabela"
                            >
                                <span style={{ fontSize: '1.8rem', marginRight: '15px' }}>⚠️</span>
                                <div>
                                    <strong style={{ display: 'block', fontSize: '1.1rem' }}>ALERTA DE PRESCRIÇÃO JURÍDICA</strong>
                                    Atenção! Você possui <strong>{qtdRiscoPrescricao} dívida(s)</strong> no 4º ano de atraso. O prazo legal máximo é de 5 anos. Ajuíze ou envie um acordo urgentemente!
                                </div>
                            </div>
                        )}

                        <div style={styles.tabsContainer}>
                            {['TODAS', 'RECENTE', 'RECUPERACAO_CRITICA', 'RISCO_PRESCRICAO', 'JUDICIAL'].map(aba => (
                                <button 
                                    key={aba}
                                    onClick={() => setFiltroAtivo(aba)}
                                    style={{
                                        ...styles.tab,
                                        borderBottom: filtroAtivo === aba ? '2px solid #10b981' : 'none',
                                        color: filtroAtivo === aba ? '#10b981' : '#94a3b8',
                                        fontWeight: filtroAtivo === aba ? 'bold' : 'normal'
                                    }}
                                >
                                    {aba.replace('_', ' ')}
                                </button>
                            ))}
                        </div>

                        {/* 🔥 BARRA DO FEIRÃO DE DESCONTO */}
                        {filtroAtivo === 'RECUPERACAO_CRITICA' && dividasFiltradas.length > 0 && (
                            <div style={styles.barraFeirao}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                                    
                                    {/* Input de Desconto */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{color: '#f59e0b', fontWeight: 'bold'}}>Desconto:</span>
                                        <input 
                                            type="number" 
                                            value={descontoFeirao} 
                                            onChange={(e) => setDescontoFeirao(e.target.value)} 
                                            style={styles.inputDesconto}
                                            min="1" max="90"
                                        />
                                        <strong style={{color: '#f59e0b'}}>%</strong>
                                    </div>

                                    {/* Input de Data Limite */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{color: '#f59e0b', fontWeight: 'bold'}}>Validade:</span>
                                        <input 
                                            type="date" 
                                            value={dataLimiteFeirao} 
                                            // Define o 'min' para hoje, impedindo datas passadas no HTML
                                            min={new Date().toISOString().split('T')[0]} 
                                            onChange={(e) => setDataLimiteFeirao(e.target.value)} 
                                            style={styles.inputData}
                                        />
                                    </div>

                                </div>
                                <button onClick={aplicarFeiraoLote} style={styles.btnAplicarLote}>
                                    🔥 Aplicar Desconto ({selecionadas.length})
                                </button>
                            </div>
                        )}

                        {loading ? (
                            <p style={{color: '#94a3b8'}}>Processando dados com inteligência...</p>
                        ) : (
                            <div style={styles.tableWrapper}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr style={styles.thRow}>
                                            {/* Coluna de Checkbox */}
                                            {filtroAtivo === 'RECUPERACAO_CRITICA' && (
                                                <th style={{...styles.th, width: '40px', textAlign: 'center'}}>
                                                    <input 
                                                        type="checkbox" 
                                                        className="checkbox-custom"
                                                        onChange={handleSelecionarTodas}
                                                        checked={dividasFiltradas.length > 0 && selecionadas.length === dividasFiltradas.length}
                                                        title="Selecionar todas"
                                                    />
                                                </th>
                                            )}
                                            <th style={styles.th}>Devedor</th>
                                            <th style={styles.th}>Atraso</th>
                                            <th style={styles.th}>Valor Atualizado</th>
                                            <th style={styles.th}>Classificação</th>
                                            <th style={styles.th}>Ação</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dividasFiltradas.length > 0 ? (
                                            dividasFiltradas.map(divida => (
                                                <tr key={divida.dividaId} style={styles.tdRow}>
                                                    {/* Checkbox individual */}
                                                    {filtroAtivo === 'RECUPERACAO_CRITICA' && (
                                                        <td style={{...styles.td, textAlign: 'center'}}>
                                                            <input 
                                                                type="checkbox" 
                                                                className="checkbox-custom"
                                                                checked={selecionadas.includes(divida.dividaId)}
                                                                onChange={() => handleSelecionarUma(divida.dividaId)}
                                                            />
                                                        </td>
                                                    )}
                                                    <td style={styles.td}>
                                                        <strong>{divida.nomeDevedor}</strong>
                                                        <span style={{display: 'block', fontSize: '0.8rem', color: '#64748b'}}>{divida.telefone}</span>
                                                    </td>
                                                    <td style={styles.td}>{divida.diasAtraso} dias</td>
                                                    <td style={styles.td}>
                                                        <strong style={{color: '#10b981'}}>{formatarMoeda(divida.valorAtualizado)}</strong>
                                                    </td>
                                                    <td style={styles.td}>
                                                        {renderBadgeClassificacao(divida.classificacaoAtraso)}
                                                    </td>
                                                    <td style={styles.td}>
                                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                            <button style={styles.btnWhatsapp}>
                                                                💬 Cobrar
                                                            </button>

                                                            {(divida.status !== 'PAGO' && divida.status !== 'EM_NEGOCIACAO') && (
                                                                <button 
                                                                    onClick={() => navigate(`/acordo/criar/${divida.dividaId}`)} 
                                                                    style={styles.btnAcordo}
                                                                    title="Criar proposta de acordo"
                                                                >
                                                                    🤝 Acordo
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={filtroAtivo === 'RECUPERACAO_CRITICA' ? 6 : 5} style={{...styles.td, textAlign: 'center', color: '#475569'}}>
                                                    Nenhuma dívida encontrada nesta classificação.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: { minHeight: '100vh', background: '#020617', color: 'white', fontFamily: 'sans-serif', padding: '20px 5%' },
    content: { maxWidth: '1200px', margin: '0 auto', width: '100%' },
    btnVoltar: { background: 'transparent', color: '#94a3b8', border: 'none', cursor: 'pointer', fontSize: '0.95rem', marginBottom: '20px', padding: 0, display: 'inline-block', transition: 'color 0.2s' },
    header: { marginBottom: '30px' },
    title: { fontSize: '1.8rem', fontWeight: '700', margin: '0 0 10px 0' },
    select: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', padding: '15px', borderRadius: '8px', color: 'white', fontSize: '1rem', width: '100%', maxWidth: '400px' },
    
    bannerAlerta: { 
        display: 'flex', 
        alignItems: 'center', 
        color: '#fca5a5', 
        border: '1px solid rgba(239, 68, 68, 0.5)', 
        padding: '15px 25px', 
        borderRadius: '12px', 
        marginBottom: '25px', 
        cursor: 'pointer',
        transition: 'transform 0.2s',
    },

    tabsContainer: { display: 'flex', gap: '15px', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: '20px', overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '5px' },
    tab: { background: 'transparent', border: 'none', padding: '10px 10px', fontSize: '0.95rem', cursor: 'pointer', transition: 'all 0.2s ease' },
    
    barraFeirao: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'rgba(245, 158, 11, 0.05)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        padding: '15px 20px',
        borderRadius: '8px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px'
    },
    inputDesconto: {
        background: '#0f172a',
        border: '1px solid rgba(245, 158, 11, 0.4)',
        color: '#f59e0b',
        padding: '8px',
        borderRadius: '6px',
        width: '65px',
        textAlign: 'center',
        fontSize: '1rem',
        fontWeight: 'bold',
        outline: 'none'
    },
    inputData: {
        background: '#0f172a',
        border: '1px solid rgba(245, 158, 11, 0.4)',
        color: '#f59e0b',
        padding: '8px',
        borderRadius: '6px',
        fontSize: '1rem',
        outline: 'none',
        cursor: 'text'
    },
    btnAplicarLote: {
        background: '#f59e0b',
        color: '#451a03',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '6px',
        fontWeight: 'bold',
        cursor: 'pointer',
        fontSize: '0.9rem',
        transition: 'background 0.2s',
        whiteSpace: 'nowrap'
    },

    tableWrapper: { background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)', overflowX: 'auto', width: '100%' },
    table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '800px' },
    thRow: { background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.1)' },
    th: { padding: '15px 20px', color: '#94a3b8', fontWeight: 'bold', fontSize: '0.9rem', whiteSpace: 'nowrap' },
    tdRow: { borderBottom: '1px solid rgba(255,255,255,0.05)', transition: 'background 0.2s' },
    td: { padding: '15px 20px', verticalAlign: 'middle' },
    btnWhatsapp: { 
        background: '#10b981', 
        color: '#022c22', 
        border: 'none', 
        padding: '8px 15px', 
        borderRadius: '6px', 
        fontWeight: 'bold', 
        cursor: 'pointer', 
        fontSize: '0.85rem', 
        whiteSpace: 'nowrap' 
    },
    btnAcordo: { 
        background: 'rgba(245, 158, 11, 0.15)',
        color: '#f59e0b',
        border: '1px solid rgba(245, 158, 11, 0.3)', 
        padding: '8px 15px', 
        borderRadius: '6px', 
        fontWeight: 'bold', 
        cursor: 'pointer', 
        fontSize: '0.85rem', 
        whiteSpace: 'nowrap',
        transition: 'all 0.2s'
    }
};

export default PainelCobranca;